import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { updateUrlSearchParams } from '@utils/search';
import logger from '@utils/logs';

interface UseFilterBuilderItemPriceProps {
    appliedValues: {
        min: number | null;
        max: number | null;
    };
    setFilterValues: (min: number | null, max: number | null) => void;
}

export const useFilterBuilderPrice: (filter: UseFilterBuilderItemPriceProps) => {
    min: number | null;
    max: number | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setMin: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setMax: any;
} = (filter) => {
    const location = useLocation();
    const navigate = useNavigate();

    const queryParams = new URLSearchParams(location.search);

    const minParamName = 'min';
    const maxParamName = 'max';

    const [min, setMin] = useState<number | null>(Number(queryParams.get(minParamName)) || null);
    const [max, setMax] = useState<number | null>(Number(queryParams.get(maxParamName)) || null);

    // if applied values change, refresh
    // this can be triggered when the filter is removed on AppliedFilters
    useEffect(() => {
        logger.log('hooks > useFilterBuilderPrice > appliedValues changed');

        if (filter.appliedValues.min === null && min !== null) {
            setMin(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.appliedValues.min]);

    useEffect(() => {
        logger.log('hooks > useFilterBuilderPrice > appliedValues changed');

        if (filter.appliedValues.max === null && max !== null) {
            setMax(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.appliedValues.max]);

    // load values according to url, initially only
    const [checkUrl, setCheckUrl] = useState<boolean>(false);

    // initially, search gets values from the url search params
    useEffect(() => {
        logger.log('hooks > useFilterBuilderPrice > location.search changed', location.search);

        if (!checkUrl) {
            const getMinParam = queryParams.get(minParamName);
            const getMaxParam = queryParams.get(maxParamName);
            if (min !== getMinParam) {
                setMin(Number(getMinParam));
            }
            if (max !== getMaxParam) {
                setMax(Number(getMaxParam));
            }

            setCheckUrl(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // update url and applied filters when values being searched change
    useEffect(() => {
        logger.log('hooks > useFilterBuilderPrice > search changed', { min });

        const getFilterParam = queryParams.get(minParamName);
        min !== getFilterParam && updateUrl(minParamName);

        // update applied filters
        filter.setFilterValues(min, max);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [min]);

    useEffect(() => {
        logger.log('hooks > useFilterBuilderPrice > search changed', { max });

        const getFilterParam = queryParams.get(maxParamName);
        max !== getFilterParam && updateUrl(maxParamName);

        // update applied filters
        filter.setFilterValues(min, max);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [max]);

    const updateUrl = (paramName: string) => {
        if (paramName === minParamName) {
            const updatedUrl = updateUrlSearchParams({ [paramName]: min ? [`${min}`] : [] });
            logger.log('hooks > useFilterBuilderPrice > updateUrl', { paramName, updatedUrl });
            navigate(updatedUrl, { replace: true });
        }

        if (paramName === maxParamName) {
            const updatedUrl = updateUrlSearchParams({ [paramName]: max ? [`${max}`] : [] });
            logger.log('hooks > useFilterBuilderPrice > updateUrl', { paramName, updatedUrl });
            navigate(updatedUrl, { replace: true });
        }
    };

    return { min, max, setMin, setMax };
};
